import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import {
    Box,
    ClickAwayListener,
    CircularProgress,
    FormControlLabel,
    Radio,
    RadioGroup,
    Tab,
    Tabs,
    Tooltip,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Trans } from '@lingui/macro';
import { useSnackbar } from 'notistack';
import { useDataService } from '../../../services/ApiDataService';
import { buildLinkUrl } from '../../links/Link';
import { GEOIP_URL, WEB_DEFAULT_THRESHOLD } from './WebTestConstants';
import copyToClipboard, { COPY_MESSAGE } from '../../../utilities/CopyToClipboardUtilities';
import WebPathDetailGraph from './WebPathDetailGraph';
import AgentTraceRouteCanvas from './AgentTraceRouteCanvas';
import { CURL_ERROR_CODES, HTTP_RESPONSE_CODES } from '../test-log-panel/TestLogContants';
import useIsPrinting from '../../../utilities/PrintUtilities';
import { useIsDarkMode } from '../../../stores/SessionStore';

const EMPTY_VALUE = '--';
const SORT_ASCENDING = 'arrow_drop_up';
const SORT_DESCENDING = 'arrow_drop_down';
const WEB_TEST_TYPE = 'Web';
const TEST_TYPE_API_URL = '/ipm/v1/open/testTypes';

const BAR_COLORS = {
    dns: '#67DC6A',
    network: '#7F6FC5',
    ssl: '#FF69B4',
    client: '#FFC833',
    app: '#5BA3CA',
    server: '#FF7943',
};

const RESULT_PASS_COLOR = '#179a52';
const RESULT_FAIL_COLOR = '#d9534f';

function toSeconds(ms) {
    return typeof ms === 'number' ? ms / 1000 : null;
}

function formatSeconds(seconds) {
    if (typeof seconds !== 'number' || Number.isNaN(seconds)) return EMPTY_VALUE;
    return Number(seconds).toFixed(3);
}

function formatRollupSeconds(ms) {
    const seconds = toSeconds(ms);
    if (seconds === null) return EMPTY_VALUE;
    return `${formatSeconds(seconds)}s`;
}

function formatSecondsTest(ms, testPassed) {
    const seconds = toSeconds(ms);
    if (seconds === null) return EMPTY_VALUE;
    if ((testPassed === false && seconds === 0) || seconds < 0) return EMPTY_VALUE;
    return `${formatSeconds(seconds)}s`;
}

function formatSizeTest(bytes, testPassed) {
    if (typeof bytes !== 'number') return EMPTY_VALUE;
    const kb = bytes / 1024;
    if (testPassed === false && (kb === 0 || Number.isNaN(kb))) return EMPTY_VALUE;
    return `${Number(kb).toFixed(2)}KB`;
}

function getFilteredRuns(agentResult) {
    const runs = (agentResult && agentResult.results) || [];
    return runs.filter((run) => run && run.data && Object.prototype.hasOwnProperty.call(run.data, 'avgResponseCode'));
}

function isTestPass(runData) {
    return Boolean(runData && runData['measurement-status'] === 'success');
}

function getResultIconColor(isPass) {
    return isPass ? RESULT_PASS_COLOR : RESULT_FAIL_COLOR;
}

function buildTestMouseoverText(runData, testNumber) {
    if (!runData) return '';
    if (isTestPass(runData)) {
        return `Test ${testNumber} of 5 - Success\nResponse Time: ${formatSecondsTest(runData.avgResponse, true)}`;
    }

    const code = runData.html_response_code;
    const isBlank = code === undefined || code === null || code === '';
    if (isBlank) return `Test ${testNumber} of 5 - Failure\nNo HTTP Response Received`;
    const errorText = buildHttpErrorText(runData);
    return `Test ${testNumber} of 5 - Failure\nResponse: ${errorText}`;
}

/**
 * Returns the human-readable error text for a failed test run, mirroring legacy
 * plm/portal/ui/lib/quickTests/web/webQuickTestsResults.directive.js (updateTestResultRow).
 * Returns '' when the run has no failure to report.
 */
function buildHttpErrorText(runData) {
    if (!runData || isTestPass(runData)) return '';
    const code = runData.html_response_code;
    if (code === undefined || code === null || code === '') {
        return 'No response received';
    }
    const numericCode = Number(code);
    if (Number.isFinite(numericCode) && numericCode >= 0) {
        const cleaned = String(code).replace('-', '');
        const desc = HTTP_RESPONSE_CODES?.[cleaned] || '';
        return desc ? `${code} ${desc}` : String(code);
    }
    if (Number.isFinite(numericCode) && numericCode < 0) {
        const cleaned = String(Math.abs(numericCode));
        return CURL_ERROR_CODES?.[cleaned] || String(code);
    }
    return String(code);
}

function getSortValue(agent, sortColumn, agentsById) {
    const rollUp = agent.rollUp || {};

    if (sortColumn === 'location') {
        const agentInfo = agentsById && agentsById[agent.agent];
        const name = agentInfo && agentInfo.name ? agentInfo.name : agent.agent;
        return String(name || '').toLowerCase();
    }

    if (sortColumn === 'hops') {
        const hops = Array.isArray(agent?.traceHops) ? agent.traceHops : getTraceHops(agent?.traceRoute);
        return getTraceHopCount(agent?.traceRoute, hops);
    }

    if (sortColumn === 'rt') return toSeconds(rollUp.avgRtMs);
    if (sortColumn === 'dns') return toSeconds(rollUp.avgDnsMs);
    if (sortColumn === 'client') return toSeconds(rollUp.avgClientMs);
    if (sortColumn === 'net') return toSeconds(rollUp.avgNetworkMs);
    if (sortColumn === 'server') return toSeconds(rollUp.avgServerMs);
    if (sortColumn === 'app') return toSeconds(rollUp.avgAppMs);
    if (sortColumn === 'ssl') return toSeconds(rollUp.avgSslMs);

    return Number.NaN;
}

function getStatusClassByThreshold(seconds, threshold) {
    if (typeof seconds !== 'number' || Number.isNaN(seconds)) return 'wtn-rt-red';
    const degraded = Number(threshold && threshold.degraded);
    const critical = Number(threshold && threshold.critical);

    if (!Number.isNaN(critical) && seconds > critical) return 'wtn-rt-red';
    if (!Number.isNaN(degraded) && seconds > degraded) return 'wtn-rt-yellow';
    return 'wtn-rt-green';
}

function getTraceHops(traceRoute) {
    if (!traceRoute) return [];
    if (Array.isArray(traceRoute.hops)) return traceRoute.hops;
    if (Array.isArray(traceRoute.hop_data)) return traceRoute.hop_data;
    if (Array.isArray(traceRoute?.trace_route?.route?.hops)) return traceRoute.trace_route.route.hops;
    if (Array.isArray(traceRoute?.route?.hops)) return traceRoute.route.hops;
    if (Array.isArray(traceRoute?.traceRoute?.route?.hops)) return traceRoute.traceRoute.route.hops;
    if (Array.isArray(traceRoute.path)) return traceRoute.path;
    return [];
}

function toTraceNumber(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return null;
}

function toBooleanFlag(value) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') {
        if (value === 1) return true;
        if (value === 0) return false;
    }
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', 'yes', 'y', '1'].includes(normalized)) return true;
        if (['false', 'no', 'n', '0'].includes(normalized)) return false;
    }
    return null;
}

function getTraceHopCount(traceRoute, hops = []) {
    const candidates = [
        traceRoute?.number_of_hops,
        traceRoute?.numberOfHops,
        traceRoute?.hopCount,
        traceRoute?.trace_route?.route?.number_of_hops,
        traceRoute?.route?.number_of_hops,
    ];

    const explicitCount = candidates.map(toTraceNumber).find((value) => value !== null && value >= 0);
    if (explicitCount !== undefined) return explicitCount;

    return Array.isArray(hops) ? hops.length : 0;
}

function normalizeStatusToken(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim().toUpperCase();
}

function isTerminalStatusToken(value) {
    const normalized = normalizeStatusToken(value);
    if (!normalized) return false;
    return [
        'COMPLETE',
        'COMPLETED',
        'DONE',
        'SUCCESS',
        'SUCCEEDED',
        'ERROR',
        'FAILED',
        'FAILURE',
        'CANCELED',
        'CANCELLED',
        'TIMEOUT',
        'TIMED_OUT',
    ].includes(normalized);
}

function isRunningStatusToken(value) {
    const normalized = normalizeStatusToken(value);
    if (!normalized) return false;
    return ['RUNNING', 'IN_PROGRESS', 'INPROGRESS', 'PENDING', 'QUEUED', 'STARTED'].includes(normalized);
}

function getTraceStatusCandidates(traceRoute = {}) {
    return [
        traceRoute.status,
        traceRoute.state,
        traceRoute.phase,
        traceRoute.result,
        traceRoute?.status?.state,
        traceRoute?.status?.status,
        traceRoute?.status?.code,
        traceRoute?.status?.message,
        traceRoute?.trace_route?.status,
        traceRoute?.trace_route?.state,
        traceRoute?.route?.status,
        traceRoute?.route?.state,
    ];
}

function getAgentTraceRoute(agent = {}) {
    return agent?.traceRoute || agent?.trace_route || {};
}

function normalizeAgentTrace(agent, isTerminal) {
    const traceRoute = getAgentTraceRoute(agent);
    const traceHops = getTraceHops(traceRoute);
    const traceHopCount = getTraceHopCount(traceRoute, traceHops);
    const traceStatuses = getTraceStatusCandidates(traceRoute);
    const hasTerminalTraceStatus = traceStatuses.some(isTerminalStatusToken);
    const hasRunningTraceStatus = traceStatuses.some(isRunningStatusToken);
    const explicitLoadingFlag = toBooleanFlag(
        agent?.loadingTraceRoute ?? agent?.loadingTrace ?? traceRoute?.loadingTraceRoute ?? traceRoute?.loading
    );
    const explicitDoneFlag = toBooleanFlag(
        agent?.traceRouteDone ??
            agent?.traceRouteComplete ??
            agent?.isTraceRouteDone ??
            traceRoute?.done ??
            traceRoute?.complete ??
            traceRoute?.completed
    );
    const hasTraceData =
        traceHops.length > 0 ||
        traceHopCount > 0 ||
        Boolean(
            traceRoute?.status ||
                traceRoute?.error ||
                traceRoute?.resolved_ip ||
                traceRoute?.resolved_dns ||
                traceRoute?.destination_ip
        );

    let loadingTraceRoute = !hasTraceData;

    if (explicitLoadingFlag !== null) {
        loadingTraceRoute = explicitLoadingFlag;
    }

    // Keep portal/ui parity: terminal test/trace status and explicit done flags force completion.
    if (isTerminal || hasTerminalTraceStatus || explicitDoneFlag === true) {
        loadingTraceRoute = false;
    } else if (explicitDoneFlag === false && !hasTraceData) {
        loadingTraceRoute = true;
    } else if (hasRunningTraceStatus) {
        loadingTraceRoute = true;
    } else if (hasTraceData) {
        loadingTraceRoute = false;
    }

    return {
        ...agent,
        traceRoute,
        traceHops,
        traceHopCount,
        loadingTraceRoute,
    };
}

function hasTraceRouteFailure(traceRoute) {
    if (!traceRoute) return false;
    const errorText = traceRoute.error;
    if (errorText === null || errorText === undefined) return false;
    return String(errorText).trim() !== '';
}

function getAgentInternalIp(agentInfo = {}, adapterType = 'default') {
    const wlanInterface = _.find(_.get(agentInfo, 'network.reportedInterfaces.interfaces'), { name: 'wlan0' });
    if (adapterType === 'wireless' && wlanInterface) {
        const wlanIp = _.get(wlanInterface, 'ipv4');
        if (typeof wlanIp === 'string' && wlanIp.trim()) return wlanIp;
    }
    const candidates = [
        _.get(agentInfo, 'network.ipv4Local'),
        _.get(agentInfo, 'network.localIp'),
        agentInfo.internalIp,
        agentInfo.internalIP,
        agentInfo.internal_ip,
        agentInfo.localIp,
        agentInfo.local_ip,
        agentInfo.ipAddress,
        agentInfo.ip,
    ];
    const matched = candidates.find((value) => typeof value === 'string' && value.trim());
    return matched || EMPTY_VALUE;
}

function getAgentLocation(agentInfo = {}) {
    const city = _.get(agentInfo, 'city.names.en');
    let country = _.get(agentInfo, 'country.names.en');
    if (country === 'United States') country = 'USA';
    const parts = [];
    if (city) parts.push(city);
    if (country) parts.push(country);
    return parts.join(',\u00a0\u00a0');
}

function getAgentHostname(agentInfo = {}) {
    const hostname = _.get(agentInfo, 'network.hostname');
    return typeof hostname === 'string' && hostname.trim() ? hostname : '';
}

function AgentRootInfo({ agentName, agentInfo, adapterType, textColor = 'inherit' }) {
    const [hovered, setHovered] = useState(false);
    const location = getAgentLocation(agentInfo);
    const hostname = getAgentHostname(agentInfo);
    const internalIp = getAgentInternalIp(agentInfo, adapterType);
    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                width: 180,
                color: textColor,
                lineHeight: '20px',
                textAlign: 'right',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
            }}
        >
            <Box component="strong" style={{ fontSize: '12pt', fontWeight: 'bold', display: 'block', cursor: 'text' }}>
                {agentName || EMPTY_VALUE}
            </Box>
            {hovered && (
                <>
                    {location && <div style={{ fontSize: '10pt' }}>{location}</div>}
                    {hostname && <div style={{ fontSize: '10pt' }}>{hostname}</div>}
                    <div style={{ fontSize: '10pt' }}>{`Internal IP: ${internalIp}`}</div>
                </>
            )}
        </Box>
    );
}

function getFirstRecordFromResponse(response) {
    if (Array.isArray(response)) return response[0];
    if (Array.isArray(response?.data)) return response.data[0];
    return response?.data;
}

const useResultTooltipStyles = makeStyles((theme) => ({
    tooltip: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: theme.shadows[3],
        borderRadius: 6,
        padding: '3px 6px 6px 6px',
        lineHeight: '16px',
        fontSize: 13,
        textAlign: 'left',
        maxWidth: 'none',
        margin: 0,
    },
    tooltipSuccess: { width: 160 },
    tooltipFailure: { width: 225 },
}));

export default function WebTestNowResults({ classes, test = {}, agentsById = {}, setAgentsById }) {
    const theme = useTheme();
    const [isDarkMode] = useIsDarkMode();
    const uiColors = useMemo(
        () => ({
            textPrimary: theme.palette.text.primary,
            textSecondary: theme.palette.text.secondary,
            textMuted: isDarkMode ? theme.palette.text.primary : '#666',
            resultLabel: theme.palette.text.primary,
            success: theme.palette.success?.main || theme.palette.primary.main,
            tableBorder: theme.palette.divider,
            panelBorder: theme.palette.divider,
            summaryBarBg: theme.palette.action.disabledBackground || theme.palette.divider,
        }),
        [isDarkMode, theme]
    );
    const resultTooltipClasses = useResultTooltipStyles();
    const dataService = useDataService();
    const { enqueueSnackbar } = useSnackbar();
    const [activeTab, setActiveTab] = useState(0);
    const [sortColumn, setSortColumn] = useState('location');
    const [sortDirection, setSortDirection] = useState(SORT_ASCENDING);
    const [expandedAgentIds, setExpandedAgentIds] = useState({});
    const [activeResultTooltipId, setActiveResultTooltipId] = useState(null);
    const [testTypeThreshold, setTestTypeThreshold] = useState(WEB_DEFAULT_THRESHOLD);
    const [pathDetailLevel, setPathDetailLevel] = useState('summary');
    const isPrinting = useIsPrinting();

    const agentResults = useMemo(() => test?.agentResults || [], [test]);
    const terminalStatuses = ['COMPLETE', 'ERROR', 'CANCELED'];
    const status = String((test && test.status) || '').toUpperCase();
    const isTerminal = terminalStatuses.indexOf(status) > -1;

    const agents = useMemo(
        () => agentResults.map((agent) => normalizeAgentTrace(agent, isTerminal)),
        [agentResults, isTerminal]
    );

    const allTraceRoutesDone = isTerminal || (agents.length > 0 && agents.every((agent) => !agent.loadingTraceRoute));

    useEffect(() => {
        let cancelled = false;

        async function loadSetupData() {
            try {
                const testTypeResponse = await dataService.get(TEST_TYPE_API_URL, {
                    query: JSON.stringify({ name: WEB_TEST_TYPE }),
                });
                const typeRecord = getFirstRecordFromResponse(testTypeResponse);

                if (!cancelled && typeRecord) {
                    const metric = _.find(_.get(typeRecord, 'metrics', []), { name: 'response_time' });
                    const threshold = _.find(
                        _.get(typeRecord, 'thresholds', []),
                        (item) => _.indexOf(_.get(item, 'metricIds', []), metric && metric.id) > -1
                    );
                    setTestTypeThreshold(threshold || WEB_DEFAULT_THRESHOLD);
                }
            } catch (e) {
                if (!cancelled) setTestTypeThreshold(WEB_DEFAULT_THRESHOLD);
            }

            try {
                const ids = _.uniq(agentResults.map((result) => result && result.agent).filter(Boolean));
                if (!ids.length) {
                    if (typeof setAgentsById === 'function') setAgentsById({});
                    return;
                }

                const results = await Promise.all(
                    ids.map((id) => {
                        const url = buildLinkUrl({ target: GEOIP_URL }, { _id: id }, null);
                        return dataService.get(url).catch(() => null);
                    })
                );
                const byId = _.keyBy(results.filter(Boolean), '_id');
                if (!cancelled && typeof setAgentsById === 'function') setAgentsById(byId);
            } catch (e) {
                if (!cancelled && typeof setAgentsById === 'function') setAgentsById({});
            }
        }

        loadSetupData();

        return () => {
            cancelled = true;
        };
    }, [agentResults, dataService, setAgentsById]);

    const sortedAgents = useMemo(() => {
        const cloned = [...agents];
        if (!sortColumn) return cloned;
        cloned.sort((a, b) => {
            const aValue = getSortValue(a, sortColumn, agentsById);
            const bValue = getSortValue(b, sortColumn, agentsById);

            if (sortColumn === 'location') {
                if (aValue < bValue) return -1;
                if (aValue > bValue) return 1;
                return 0;
            }

            const aNum = Number(aValue);
            const bNum = Number(bValue);
            if (Number.isNaN(aNum) || Number.isNaN(bNum)) return 0;
            if (aNum < bNum) return -1;
            if (aNum > bNum) return 1;
            return 0;
        });

        if (sortDirection === SORT_DESCENDING) cloned.reverse();
        return cloned;
    }, [agents, sortColumn, sortDirection, agentsById]);

    const allExpanded = sortedAgents.length > 0 && sortedAgents.every((agent) => expandedAgentIds[agent.agent]);

    function handleSort(columnId) {
        if (columnId === sortColumn) {
            setSortDirection(sortDirection === SORT_ASCENDING ? SORT_DESCENDING : SORT_ASCENDING);
            return;
        }
        setSortColumn(columnId);
        setSortDirection(SORT_ASCENDING);
    }

    function toggleNestedTable(agentId) {
        setExpandedAgentIds((prev) => ({ ...prev, [agentId]: !prev[agentId] }));
    }

    function toggleAll() {
        if (allExpanded) {
            setExpandedAgentIds({});
            return;
        }
        const next = {};
        sortedAgents.forEach((agent) => {
            next[agent.agent] = true;
        });
        setExpandedAgentIds(next);
    }

    function toggleResultTooltip(tooltipId) {
        setActiveResultTooltipId((prev) => (prev === tooltipId ? null : tooltipId));
    }

    function closeResultTooltip() {
        setActiveResultTooltipId(null);
    }

    useEffect(() => {
        if (!activeResultTooltipId) return undefined;

        const handleDocumentMouseDown = (event) => {
            const trigger = event.target?.closest?.('[data-result-tooltip-trigger="true"]');
            if (trigger) return;
            setActiveResultTooltipId(null);
        };

        document.addEventListener('mousedown', handleDocumentMouseDown, true);
        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown, true);
        };
    }, [activeResultTooltipId]);

    return (
        <div id="wtnTabsContainer" className="tabs-container">
            <Tabs
                className={isPrinting ? classes.printHiddenTabs : 'tvl-tabs'}
                value={activeTab}
                onChange={(event, tab) => setActiveTab(tab)}
            >
                <Tab className={classes.tabContent} label={<Trans>Test Summary</Trans>} />
                <Tab className={classes.tabContent} label={<Trans>Test Details</Trans>} />
                <Tab className={classes.tabContent} label={<Trans>Path Details</Trans>} />
            </Tabs>

            {(activeTab === 0 || isPrinting) && (
                <ClickAwayListener onClickAway={closeResultTooltip}>
                    <div className={classes.tabContent}>
                        <div className={classes.scrollX}>
                            <div id="summary-container" className="summary-container">
                                {isPrinting && (
                                    <div className={classes.printTabHeader}>
                                        <Trans>Test Summary</Trans>
                                    </div>
                                )}

                                {agents.map((agent) => {
                                    const rollUp = agent.rollUp || {};
                                    const runs = getFilteredRuns(agent);
                                    const agentInfo = agentsById && agentsById[agent.agent];
                                    const agentName = (agentInfo && agentInfo.name) || agent.agent;
                                    const rtSeconds = toSeconds(rollUp.avgRtMs);
                                    const traceRoute = agent.traceRoute || {};
                                    const traceRouteUnavailable = hasTraceRouteFailure(traceRoute);
                                    const dnsSummaryText = formatRollupSeconds(rollUp.avgDnsMs);
                                    const networkSummaryText = formatRollupSeconds(rollUp.avgNetworkMs);
                                    const sslSummaryText = formatRollupSeconds(rollUp.avgSslMs);
                                    const clientSummaryText = formatRollupSeconds(rollUp.avgClientMs);
                                    const appSummaryText = formatRollupSeconds(rollUp.avgAppMs);
                                    const serverSummaryText = formatRollupSeconds(rollUp.avgServerMs);
                                    const traceTestName = test?.testConfig?.url || test?.name || '';

                                    return (
                                        <div
                                            key={agent.agent}
                                            className="probe-container"
                                            id={`probe-container-${agent.agent}`}
                                            style={{ margin: '24px 0' }}
                                        >
                                            <Box display="flex" alignItems="flex-start" style={{ width: '100%' }}>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    style={{ flex: 1, minWidth: 0, paddingRight: 16 }}
                                                >
                                                    <AgentRootInfo
                                                        agentName={agentName}
                                                        agentInfo={agentInfo}
                                                        adapterType={test?.adapterType}
                                                        textColor={uiColors.textMuted}
                                                    />
                                                </Box>

                                                <Box style={{ flex: '0 0 auto' }}>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        style={{ gap: 12, marginBottom: 10 }}
                                                    >
                                                        <Box
                                                            id={`bar-${agent.agent}`}
                                                            className="response-time-bar bars"
                                                            style={{
                                                                width: 380,
                                                                height: 14,
                                                                display: 'flex',
                                                                overflow: 'hidden',
                                                                backgroundColor: uiColors.summaryBarBg,
                                                            }}
                                                        >
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`DNS: ${dnsSummaryText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${rollUp.dnsPctWidth || 0}%`,
                                                                        backgroundColor: BAR_COLORS.dns,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`Network: ${networkSummaryText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${
                                                                            rollUp.networkPctWidth ||
                                                                            rollUp.netPctWidth ||
                                                                            0
                                                                        }%`,
                                                                        backgroundColor: BAR_COLORS.network,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`SSL: ${sslSummaryText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${rollUp.sslPctWidth || 0}%`,
                                                                        backgroundColor: BAR_COLORS.ssl,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`Client: ${clientSummaryText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${rollUp.clientPctWidth || 0}%`,
                                                                        backgroundColor: BAR_COLORS.client,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`Application: ${appSummaryText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${rollUp.appPctWidth || 0}%`,
                                                                        backgroundColor: BAR_COLORS.app,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`Server: ${serverSummaryText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${rollUp.serverPctWidth || 0}%`,
                                                                        backgroundColor: BAR_COLORS.server,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </Box>

                                                        <Box
                                                            className={getStatusClassByThreshold(
                                                                rtSeconds,
                                                                testTypeThreshold
                                                            )}
                                                            style={{ fontSize: 14 }}
                                                        >
                                                            {`${formatSeconds(rtSeconds)}s`}
                                                        </Box>

                                                        <Box
                                                            style={{
                                                                fontSize: 14,
                                                                fontWeight: 700,
                                                                color: uiColors.resultLabel,
                                                            }}
                                                        >
                                                            <Trans>Result:</Trans>
                                                        </Box>

                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            style={{
                                                                gap: 8,
                                                                visibility: traceRouteUnavailable
                                                                    ? 'hidden'
                                                                    : 'visible',
                                                            }}
                                                        >
                                                            {[1, 2, 3, 4, 5].map((i) => {
                                                                const runData = runs[i - 1] && runs[i - 1].data;
                                                                const isPass = isTestPass(runData);
                                                                const tooltipId = `${agent.agent}-summary-${i}`;
                                                                return (
                                                                    <Tooltip
                                                                        key={`${agent.agent}-${i}`}
                                                                        title={
                                                                            <span style={{ whiteSpace: 'pre-line' }}>
                                                                                {buildTestMouseoverText(runData, i)}
                                                                            </span>
                                                                        }
                                                                        classes={{
                                                                            tooltip: `${resultTooltipClasses.tooltip} ${
                                                                                isPass
                                                                                    ? resultTooltipClasses.tooltipSuccess
                                                                                    : resultTooltipClasses.tooltipFailure
                                                                            }`,
                                                                        }}
                                                                        open={activeResultTooltipId === tooltipId}
                                                                        placement="top"
                                                                        disableHoverListener
                                                                        disableFocusListener
                                                                        disableTouchListener
                                                                    >
                                                                        <i
                                                                            className="material-icons"
                                                                            role="button"
                                                                            tabIndex={0}
                                                                            data-result-tooltip-trigger="true"
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                toggleResultTooltip(tooltipId);
                                                                            }}
                                                                            onKeyDown={(event) => {
                                                                                if (
                                                                                    event.key === 'Enter' ||
                                                                                    event.key === ' '
                                                                                ) {
                                                                                    event.preventDefault();
                                                                                    event.stopPropagation();
                                                                                    toggleResultTooltip(tooltipId);
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                fontSize: 24,
                                                                                color: getResultIconColor(isPass),
                                                                                cursor:
                                                                                    activeResultTooltipId === tooltipId
                                                                                        ? 'text'
                                                                                        : 'pointer',
                                                                            }}
                                                                        >
                                                                            stop
                                                                        </i>
                                                                    </Tooltip>
                                                                );
                                                            })}
                                                        </Box>
                                                    </Box>

                                                    {!agent.loadingTraceRoute && (
                                                        <Box style={{ position: 'relative', minHeight: 120 }}>
                                                            <AgentTraceRouteCanvas
                                                                agent={agent}
                                                                traceRoute={traceRoute}
                                                                testName={traceTestName}
                                                                isDarkMode={isDarkMode}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Box style={{ flex: 1, minWidth: 0 }} />
                                            </Box>

                                            {agent.loadingTraceRoute && (
                                                <Box style={{ marginLeft: 342, marginTop: 8 }}>
                                                    <CircularProgress
                                                        size={32}
                                                        className="test-now web ngp-spinner"
                                                        id={`progress-${agent.agent}`}
                                                    />
                                                </Box>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </ClickAwayListener>
            )}

            {(activeTab === 1 || isPrinting) && (
                <ClickAwayListener onClickAway={closeResultTooltip}>
                    <div className={classes.tabContent}>
                        {isPrinting && (
                            <div className={classes.printTabHeader} id="tabDetails">
                                <Trans>Test Details</Trans>
                            </div>
                        )}
                        {!isPrinting && (
                            <Box
                                style={{
                                    margin: '6px 0 10px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 12,
                                    fontSize: 14,
                                    fontWeight: 600,
                                }}
                            >
                                {[
                                    ['DNS', BAR_COLORS.dns],
                                    ['Network', BAR_COLORS.network],
                                    ['SSL', BAR_COLORS.ssl],
                                    ['Client', BAR_COLORS.client],
                                    ['Application', BAR_COLORS.app],
                                    ['Server', BAR_COLORS.server],
                                ].map(([label, color]) => (
                                    <Box key={label} display="flex" alignItems="center" style={{ gap: 8 }}>
                                        <span
                                            style={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: 2,
                                                background: color,
                                                display: 'inline-block',
                                            }}
                                        />
                                        <span>{label}</span>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        <div
                            className="data-table"
                            id="test-now-body-container"
                            style={{ fontSize: 14, overflowX: 'auto' }}
                        >
                            <table
                                id="quickTestDataTbl"
                                style={{
                                    width: 'auto',
                                    borderCollapse: 'collapse',
                                    tableLayout: 'fixed',
                                }}
                            >
                                <colgroup>
                                    <col style={{ width: 34 }} />
                                    <col style={{ width: 220 }} />
                                    <col style={{ width: 160 }} />
                                    <col style={{ width: 58 }} />
                                    <col style={{ width: 120 }} />
                                    <col style={{ width: 76 }} />
                                    <col style={{ width: 84 }} />
                                    <col style={{ width: 76 }} />
                                    <col style={{ width: 76 }} />
                                    <col style={{ width: 100 }} />
                                    <col style={{ width: 76 }} />
                                    <col />
                                </colgroup>

                                <thead>
                                    <tr style={{ borderBottom: `1px solid ${uiColors.tableBorder}` }}>
                                        <th
                                            style={{
                                                textAlign: 'center',
                                                padding: '6px 2px',
                                                color: uiColors.textSecondary,
                                            }}
                                        >
                                            {!isPrinting && (
                                                <i
                                                    id="toggle-all"
                                                    className="material-icons"
                                                    title="Expand/Collapse all sections"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={toggleAll}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                            event.preventDefault();
                                                            toggleAll();
                                                        }
                                                    }}
                                                >
                                                    {allExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
                                                </i>
                                            )}
                                        </th>
                                        {[
                                            ['location', 'Location'],
                                            ['tests', 'Result'],
                                            ['hops', 'Hops'],
                                            ['rt', 'Response Time'],
                                            ['dns', 'DNS'],
                                            ['net', 'Network'],
                                            ['ssl', 'SSL'],
                                            ['client', 'Client'],
                                            ['app', 'Application'],
                                            ['server', 'Server'],
                                        ].map(([id, label]) => {
                                            const sortable = id !== 'tests';
                                            return (
                                                <th
                                                    key={id}
                                                    onClick={sortable ? () => handleSort(id) : undefined}
                                                    style={{
                                                        padding: '6px 4px 8px',
                                                        textAlign: 'center',
                                                        color: uiColors.textSecondary,
                                                        fontSize: 14,
                                                        fontWeight: 700,
                                                        cursor: sortable ? 'pointer' : 'default',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <Box display="inline-flex" alignItems="center" style={{ gap: 4 }}>
                                                        <span>{label}</span>
                                                        <i className="material-icons" style={{ fontSize: 16 }}>
                                                            {sortColumn === id ? sortDirection : ''}
                                                        </i>
                                                    </Box>
                                                </th>
                                            );
                                        })}
                                        <th />
                                    </tr>
                                </thead>

                                {sortedAgents.map((agent, agentIndex) => {
                                    const rollUp = agent.rollUp || {};
                                    const runs = getFilteredRuns(agent);
                                    const hops = getTraceHopCount(agent.traceRoute, agent.traceHops);
                                    const isAllTestsFailed = rollUp.pass === 0;
                                    const agentInfo = agentsById && agentsById[agent.agent];
                                    const agentName = (agentInfo && agentInfo.name) || agent.agent;
                                    const dnsBarText = formatRollupSeconds(rollUp.avgDnsMs);
                                    const networkBarText = formatRollupSeconds(rollUp.avgNetworkMs);
                                    const sslBarText = formatRollupSeconds(rollUp.avgSslMs);
                                    const clientBarText = formatRollupSeconds(rollUp.avgClientMs);
                                    const appBarText = formatRollupSeconds(rollUp.avgAppMs);
                                    const serverBarText = formatRollupSeconds(rollUp.avgServerMs);
                                    const showAgentDivider = agentIndex < sortedAgents.length - 1;

                                    return (
                                        <tbody key={agent.agent}>
                                            <tr id={`tr-${agent.agent}`}>
                                                <td
                                                    id={`icon-${agent.agent}`}
                                                    style={{ textAlign: 'center', padding: '6px 2px' }}
                                                >
                                                    {!isPrinting && (
                                                        <i
                                                            className="material-icons"
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => toggleNestedTable(agent.agent)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter' || event.key === ' ') {
                                                                    event.preventDefault();
                                                                    toggleNestedTable(agent.agent);
                                                                }
                                                            }}
                                                        >
                                                            {expandedAgentIds[agent.agent]
                                                                ? 'keyboard_arrow_down'
                                                                : 'keyboard_arrow_right'}
                                                        </i>
                                                    )}
                                                </td>

                                                <td style={{ padding: '6px 4px' }}>
                                                    <div
                                                        style={{
                                                            fontSize: 14,
                                                            fontWeight: 500,
                                                            color: uiColors.textPrimary,
                                                        }}
                                                    >
                                                        {agentName}
                                                    </div>
                                                </td>

                                                <td style={{ textAlign: 'center', padding: '6px 4px' }}>
                                                    {[1, 2, 3, 4, 5].map((i) => {
                                                        const runData = runs[i - 1] && runs[i - 1].data;
                                                        const isPass = isTestPass(runData);
                                                        const tooltipId = `${agent.agent}-details-summary-${i}`;
                                                        return (
                                                            <span
                                                                key={`${agent.agent}-summary-${i}`}
                                                                className="test-result details-result"
                                                                id={`${agent.agent}-test-${i}`}
                                                            >
                                                                <Tooltip
                                                                    title={
                                                                        <span style={{ whiteSpace: 'pre-line' }}>
                                                                            {buildTestMouseoverText(runData, i)}
                                                                        </span>
                                                                    }
                                                                    classes={{
                                                                        tooltip: `${resultTooltipClasses.tooltip} ${
                                                                            isPass
                                                                                ? resultTooltipClasses.tooltipSuccess
                                                                                : resultTooltipClasses.tooltipFailure
                                                                        }`,
                                                                    }}
                                                                    open={activeResultTooltipId === tooltipId}
                                                                    placement="top"
                                                                    disableHoverListener
                                                                    disableFocusListener
                                                                    disableTouchListener
                                                                >
                                                                    <i
                                                                        className="material-icons"
                                                                        role="button"
                                                                        tabIndex={0}
                                                                        data-result-tooltip-trigger="true"
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            toggleResultTooltip(tooltipId);
                                                                        }}
                                                                        onKeyDown={(event) => {
                                                                            if (
                                                                                event.key === 'Enter' ||
                                                                                event.key === ' '
                                                                            ) {
                                                                                event.preventDefault();
                                                                                event.stopPropagation();
                                                                                toggleResultTooltip(tooltipId);
                                                                            }
                                                                        }}
                                                                        style={{
                                                                            color: getResultIconColor(isPass),
                                                                            cursor:
                                                                                activeResultTooltipId === tooltipId
                                                                                    ? 'text'
                                                                                    : 'pointer',
                                                                            fontSize: 20,
                                                                            margin: '0 1px',
                                                                        }}
                                                                    >
                                                                        stop
                                                                    </i>
                                                                </Tooltip>
                                                            </span>
                                                        );
                                                    })}
                                                </td>

                                                <td style={{ textAlign: 'center', fontSize: 14 }}>{hops}</td>
                                                <td
                                                    style={{
                                                        textAlign: 'center',
                                                        color: uiColors.success,
                                                        fontWeight: 700,
                                                        fontSize: 14,
                                                    }}
                                                >
                                                    {isAllTestsFailed
                                                        ? EMPTY_VALUE
                                                        : `${formatSeconds(toSeconds(rollUp.avgRtMs))}s`}
                                                </td>
                                                <td style={{ textAlign: 'center', fontSize: 14 }}>
                                                    {isAllTestsFailed
                                                        ? EMPTY_VALUE
                                                        : `${formatSeconds(toSeconds(rollUp.avgDnsMs))}s`}
                                                </td>
                                                <td style={{ textAlign: 'center', fontSize: 14 }}>
                                                    {isAllTestsFailed
                                                        ? EMPTY_VALUE
                                                        : `${formatSeconds(toSeconds(rollUp.avgNetworkMs))}s`}
                                                </td>
                                                <td style={{ textAlign: 'center', fontSize: 14 }}>
                                                    {isAllTestsFailed || !rollUp.avgSslMs
                                                        ? EMPTY_VALUE
                                                        : `${formatSeconds(toSeconds(rollUp.avgSslMs))}s`}
                                                </td>
                                                <td style={{ textAlign: 'center', fontSize: 14 }}>
                                                    {isAllTestsFailed
                                                        ? EMPTY_VALUE
                                                        : `${formatSeconds(toSeconds(rollUp.avgClientMs))}s`}
                                                </td>
                                                <td style={{ textAlign: 'center', fontSize: 14 }}>
                                                    {isAllTestsFailed
                                                        ? EMPTY_VALUE
                                                        : `${formatSeconds(toSeconds(rollUp.avgAppMs))}s`}
                                                </td>
                                                <td style={{ textAlign: 'center', fontSize: 14 }}>
                                                    {isAllTestsFailed
                                                        ? EMPTY_VALUE
                                                        : `${formatSeconds(toSeconds(rollUp.avgServerMs))}s`}
                                                </td>
                                                <td />
                                            </tr>

                                            {!isPrinting && (
                                                <tr>
                                                    <td />
                                                    <td colSpan="11" style={{ padding: '0 6px 6px 4px' }}>
                                                        <Box display="flex" style={{ height: 18, overflow: 'hidden' }}>
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`DNS: ${dnsBarText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${rollUp.dnsPctWidth || 0}%`,
                                                                        backgroundColor: BAR_COLORS.dns,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`Network: ${networkBarText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${
                                                                            rollUp.networkPctWidth ||
                                                                            rollUp.netPctWidth ||
                                                                            0
                                                                        }%`,
                                                                        backgroundColor: BAR_COLORS.network,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`SSL: ${sslBarText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${rollUp.sslPctWidth || 0}%`,
                                                                        backgroundColor: BAR_COLORS.ssl,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`Client: ${clientBarText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${rollUp.clientPctWidth || 0}%`,
                                                                        backgroundColor: BAR_COLORS.client,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`Application: ${appBarText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${rollUp.appPctWidth || 0}%`,
                                                                        backgroundColor: BAR_COLORS.app,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip
                                                                title={
                                                                    <span
                                                                        style={{ fontSize: 14, lineHeight: '20px' }}
                                                                    >{`Server: ${serverBarText}`}</span>
                                                                }
                                                                arrow
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: `${rollUp.serverPctWidth || 0}%`,
                                                                        backgroundColor: BAR_COLORS.server,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </Box>
                                                    </td>
                                                </tr>
                                            )}

                                            {(expandedAgentIds[agent.agent] || isPrinting) && (
                                                <tr id={`tr-agent-${agent.agent}`}>
                                                    <td />
                                                    <td colSpan="11" style={{ padding: '0 4px 8px' }}>
                                                        <table
                                                            style={{
                                                                width: 'auto',
                                                                borderCollapse: 'collapse',
                                                                tableLayout: 'fixed',
                                                            }}
                                                            className="quick-test-results-table-nested"
                                                        >
                                                            <thead>
                                                                <tr>
                                                                    {[
                                                                        'Test',
                                                                        'Result',
                                                                        'Size',
                                                                        'Response Time',
                                                                        'DNS',
                                                                        'Network',
                                                                        'SSL',
                                                                        'Client',
                                                                        'Application',
                                                                        'Server',
                                                                    ].map((label) => (
                                                                        <th
                                                                            key={label}
                                                                            style={{
                                                                                padding: '6px 14px',
                                                                                textAlign: 'center',
                                                                                fontSize: 14,
                                                                                fontWeight: 700,
                                                                                color: uiColors.textPrimary,
                                                                            }}
                                                                        >
                                                                            {label}
                                                                        </th>
                                                                    ))}
                                                                    <th
                                                                        style={{
                                                                            padding: '6px 14px',
                                                                            textAlign: 'left',
                                                                            fontSize: 14,
                                                                            fontWeight: 700,
                                                                            color: uiColors.textPrimary,
                                                                        }}
                                                                    >
                                                                        {runs.some(
                                                                            (r) => r && r.data && !isTestPass(r.data)
                                                                        )
                                                                            ? 'Error'
                                                                            : ''}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {[0, 1, 2, 3, 4].map((idx) => {
                                                                    const run = runs[idx];
                                                                    const runData = run && run.data;
                                                                    const isPass = isTestPass(runData);
                                                                    const tooltipId = `${
                                                                        agent.agent
                                                                    }-details-expanded-${idx + 1}`;
                                                                    return (
                                                                        <tr
                                                                            key={`${agent.agent}-detail-${idx}`}
                                                                            id={`tr-agent-${agent.agent}-result-${idx}`}
                                                                        >
                                                                            <td
                                                                                style={{
                                                                                    textAlign: 'center',
                                                                                    padding: '4px 16px',
                                                                                    fontSize: 14,
                                                                                }}
                                                                            >
                                                                                {idx + 1}
                                                                            </td>
                                                                            <td
                                                                                style={{
                                                                                    textAlign: 'center',
                                                                                    padding: '4px 16px',
                                                                                }}
                                                                            >
                                                                                <span
                                                                                    className="passSpan details-result-expanded test-result"
                                                                                    id={`${agent.agent}-test-${
                                                                                        idx + 1
                                                                                    }`}
                                                                                >
                                                                                    {runData && (
                                                                                        <Tooltip
                                                                                            title={
                                                                                                <span
                                                                                                    style={{
                                                                                                        whiteSpace:
                                                                                                            'pre-line',
                                                                                                    }}
                                                                                                >
                                                                                                    {buildTestMouseoverText(
                                                                                                        runData,
                                                                                                        idx + 1
                                                                                                    )}
                                                                                                </span>
                                                                                            }
                                                                                            classes={{
                                                                                                tooltip: `${
                                                                                                    resultTooltipClasses.tooltip
                                                                                                } ${
                                                                                                    isPass
                                                                                                        ? resultTooltipClasses.tooltipSuccess
                                                                                                        : resultTooltipClasses.tooltipFailure
                                                                                                }`,
                                                                                            }}
                                                                                            open={
                                                                                                activeResultTooltipId ===
                                                                                                tooltipId
                                                                                            }
                                                                                            placement="top"
                                                                                            disableHoverListener
                                                                                            disableFocusListener
                                                                                            disableTouchListener
                                                                                        >
                                                                                            <i
                                                                                                className="material-icons"
                                                                                                role="button"
                                                                                                tabIndex={0}
                                                                                                data-result-tooltip-trigger="true"
                                                                                                onClick={(event) => {
                                                                                                    event.stopPropagation();
                                                                                                    toggleResultTooltip(
                                                                                                        tooltipId
                                                                                                    );
                                                                                                }}
                                                                                                onKeyDown={(event) => {
                                                                                                    if (
                                                                                                        event.key ===
                                                                                                            'Enter' ||
                                                                                                        event.key ===
                                                                                                            ' '
                                                                                                    ) {
                                                                                                        event.preventDefault();
                                                                                                        event.stopPropagation();
                                                                                                        toggleResultTooltip(
                                                                                                            tooltipId
                                                                                                        );
                                                                                                    }
                                                                                                }}
                                                                                                style={{
                                                                                                    color: getResultIconColor(
                                                                                                        isPass
                                                                                                    ),
                                                                                                    cursor:
                                                                                                        activeResultTooltipId ===
                                                                                                        tooltipId
                                                                                                            ? 'text'
                                                                                                            : 'pointer',
                                                                                                    fontSize: 20,
                                                                                                }}
                                                                                            >
                                                                                                stop
                                                                                            </i>
                                                                                        </Tooltip>
                                                                                    )}
                                                                                </span>
                                                                            </td>
                                                                            <td
                                                                                style={{
                                                                                    textAlign: 'center',
                                                                                    fontSize: 14,
                                                                                    padding: '4px 16px',
                                                                                }}
                                                                            >
                                                                                {runData
                                                                                    ? formatSizeTest(
                                                                                          runData.response_size,
                                                                                          isPass
                                                                                      )
                                                                                    : '\u00a0'}
                                                                            </td>
                                                                            <td
                                                                                style={{
                                                                                    textAlign: 'center',
                                                                                    fontSize: 14,
                                                                                    color: uiColors.success,
                                                                                    fontWeight: 700,
                                                                                    padding: '4px 16px',
                                                                                }}
                                                                            >
                                                                                {runData
                                                                                    ? formatSecondsTest(
                                                                                          runData.avgResponse,
                                                                                          isPass
                                                                                      )
                                                                                    : '\u00a0'}
                                                                            </td>
                                                                            <td
                                                                                style={{
                                                                                    textAlign: 'center',
                                                                                    fontSize: 14,
                                                                                    padding: '4px 16px',
                                                                                }}
                                                                            >
                                                                                {runData
                                                                                    ? formatSecondsTest(
                                                                                          runData.avgDns,
                                                                                          isPass
                                                                                      )
                                                                                    : '\u00a0'}
                                                                            </td>
                                                                            <td
                                                                                style={{
                                                                                    textAlign: 'center',
                                                                                    fontSize: 14,
                                                                                    padding: '4px 16px',
                                                                                }}
                                                                            >
                                                                                {runData
                                                                                    ? formatSecondsTest(
                                                                                          runData.avgNetwork,
                                                                                          isPass
                                                                                      )
                                                                                    : '\u00a0'}
                                                                            </td>
                                                                            <td
                                                                                style={{
                                                                                    textAlign: 'center',
                                                                                    fontSize: 14,
                                                                                    padding: '4px 16px',
                                                                                }}
                                                                            >
                                                                                {(() => {
                                                                                    if (!runData) return '\u00a0';
                                                                                    if (!runData.avgSsl)
                                                                                        return EMPTY_VALUE;
                                                                                    return formatSecondsTest(
                                                                                        runData.avgSsl,
                                                                                        isPass
                                                                                    );
                                                                                })()}
                                                                            </td>
                                                                            <td
                                                                                style={{
                                                                                    textAlign: 'center',
                                                                                    fontSize: 14,
                                                                                    padding: '4px 16px',
                                                                                }}
                                                                            >
                                                                                {runData
                                                                                    ? formatSecondsTest(
                                                                                          runData.avgClient,
                                                                                          isPass
                                                                                      )
                                                                                    : '\u00a0'}
                                                                            </td>
                                                                            <td
                                                                                style={{
                                                                                    textAlign: 'center',
                                                                                    fontSize: 14,
                                                                                    padding: '4px 16px',
                                                                                }}
                                                                            >
                                                                                {runData
                                                                                    ? formatSecondsTest(
                                                                                          runData.avgApp,
                                                                                          isPass
                                                                                      )
                                                                                    : '\u00a0'}
                                                                            </td>
                                                                            <td
                                                                                style={{
                                                                                    textAlign: 'center',
                                                                                    fontSize: 14,
                                                                                    padding: '4px 16px',
                                                                                }}
                                                                            >
                                                                                {runData
                                                                                    ? formatSecondsTest(
                                                                                          runData.avgServer,
                                                                                          isPass
                                                                                      )
                                                                                    : '\u00a0'}
                                                                            </td>
                                                                            <td
                                                                                style={{
                                                                                    textAlign: 'left',
                                                                                    fontSize: 14,
                                                                                    paddingLeft: 6,
                                                                                    whiteSpace: 'normal',
                                                                                    wordBreak: 'break-word',
                                                                                }}
                                                                            >
                                                                                {runData && !isPass
                                                                                    ? buildHttpErrorText(runData) ||
                                                                                      'No response received'
                                                                                    : '\u00a0'}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                            )}

                                            {showAgentDivider && (
                                                <tr>
                                                    <td colSpan="12" style={{ padding: 0 }}>
                                                        <div
                                                            style={{
                                                                borderBottom: `1px solid ${uiColors.tableBorder}`,
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    );
                                })}
                            </table>
                        </div>
                    </div>
                </ClickAwayListener>
            )}

            {(activeTab === 2 || isPrinting) && (
                <div className={classes.tabContent} style={{ paddingTop: 0 }}>
                    {isPrinting && (
                        <div className={classes.printTabHeader} id="tabTraceRoute">
                            <Trans>Path Details</Trans>
                        </div>
                    )}

                    <Box
                        display="flex"
                        alignItems="center"
                        style={{
                            gap: 16,
                            padding: '10px 16px',
                            borderTop: `1px solid ${uiColors.panelBorder}`,
                            borderBottom: `1px solid ${uiColors.panelBorder}`,
                            marginBottom: 10,
                            display: isPrinting ? 'none' : 'flex',
                        }}
                    >
                        <span style={{ fontSize: 14 }}>
                            <Trans>Detail Level:</Trans>
                        </span>
                        <RadioGroup
                            row
                            value={pathDetailLevel}
                            onChange={(event) => setPathDetailLevel(event.target.value)}
                        >
                            <FormControlLabel
                                value="summary"
                                control={<Radio color="primary" />}
                                label={<Trans>Summary</Trans>}
                            />
                            <FormControlLabel
                                value="detailed"
                                control={<Radio color="primary" />}
                                label={<Trans>Detailed</Trans>}
                            />
                        </RadioGroup>
                    </Box>

                    <div id="horizontal-trace-container">
                        {!allTraceRoutesDone && (
                            <div className="trace-loading">
                                <div className="info">
                                    <Trans>Gathering route information...</Trans>
                                </div>
                            </div>
                        )}

                        <Box id="visualize-container" style={{ padding: '0 8px' }}>
                            <WebPathDetailGraph
                                agents={agents}
                                agentsById={agentsById}
                                detailLevel={isPrinting ? 'summary' : pathDetailLevel}
                                graphData={test?.graphData}
                                onNodeClick={(nodeData) => {
                                    if (!nodeData) return;
                                    const text = nodeData.tooltipText || nodeData.label || '';
                                    if (text) {
                                        try {
                                            copyToClipboard(text);
                                            enqueueSnackbar(COPY_MESSAGE);
                                        } catch (_e) {
                                            /* silent */
                                        }
                                    }
                                }}
                            />
                        </Box>
                    </div>
                </div>
            )}
        </div>
    );
}
